import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, FileText, BarChart3, Activity, Clock, Loader2, 
  CheckCircle2, Sparkles, Plus, Eye, Scale, Gavel, Users,
  Search, TrendingUp, Calculator, Briefcase, FolderOpen,
  Target, Building2, ChevronRight, ArrowRight, Zap,
  BookOpen, Truck, MessageSquare, Globe, MapPin,
  Navigation, DollarSign
} from "lucide-react";
import { API } from "@/constants";
import { useAuth } from "@/contexts/AuthContext";

const AdminDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/overview`, { withCredentials: true });
        setData(res.data);
      } catch (e) {
        console.error(e);
        setData({ stats: {}, recent_cases: [], recent_reports: [], recent_scorecards: [], recent_investigations: [] });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );

  const stats = data?.stats || {};
  const ps = stats.practice_suite || {};

  const TOOL_CATEGORIES = [
    {
      title: "Case Management",
      desc: "Onboard and manage cases",
      color: "from-blue-600 to-blue-700",
      icon: Briefcase,
      tools: [
        { to: "/tools/case-starter", label: "Case Starter", desc: "Quick AI case analysis", badge: "NEW" },
        { to: "/tools/litix-wizard", label: "Litix Wizard", desc: "6-step case onboarding", badge: "CORE" },
        { to: "/tools/case-builder", label: "Case Builder", desc: "Guided case construction" },
        { to: "/scanner", label: "Doc Scanner", desc: "Camera AI analysis", badge: "NEW" },
        { to: "/tools/case-summary", label: "Case Summary", desc: "Generate client memos" },
      ]
    },
    {
      title: "Trial Preparation",
      desc: "Opening statements, closing arguments & more",
      color: "from-red-600 to-red-700",
      icon: Gavel,
      tools: [
        { to: "/tools/opening", label: "Opening Statement", desc: "Trial-ready openings" },
        { to: "/tools/closing", label: "Closing Argument", desc: "Compelling closings" },
        { to: "/tools/trial-organizer", label: "Trial Organizer", desc: "Complete trial binder" },
        { to: "/tools/deposition", label: "Depositions", desc: "Examination outlines" },
      ]
    },
    {
      title: "Settlement & Negotiation",
      desc: "Demand letters, mediation & negotiation",
      color: "from-emerald-600 to-emerald-700",
      icon: Scale,
      tools: [
        { to: "/tools/settlement-negotiation", label: "Settlement Strategy", desc: "Negotiation analysis" },
        { to: "/tools/demand-letter", label: "Demand Letter", desc: "Professional demands" },
        { to: "/tools/mediation", label: "Mediation Brief", desc: "Position statements" },
        { to: "/tools/motion", label: "Motion Practice", desc: "MSJ, dismiss, compel" },
      ]
    },
    {
      title: "Litigation GPS",
      desc: "Navigate cases with real-time intelligence",
      color: "from-cyan-600 to-blue-700",
      icon: Navigation,
      tools: [
        { to: "/tools/litigation-gps", label: "Litigation GPS", desc: "5-module intelligence hub", badge: "NEW" },
        { to: "/tools/investigator", label: "The Investigator", desc: "Party intelligence", badge: "PREMIUM" },
        { to: "/tools/request-center", label: "Request Center", desc: "Multi-source intel" },
        { to: "/tools/medical-intel", label: "Medical Intel", desc: "Provider fraud detection" },
        { to: "/tools/carrier-scorecard", label: "Carrier Scorecard", desc: "Insurance carrier intel" },
      ]
    },
    {
      title: "Financial Engine",
      desc: "Case valuation, cost forecasting & ROI",
      color: "from-emerald-600 to-emerald-700",
      icon: DollarSign,
      tools: [
        { to: "/tools/financial-engine", label: "Financial Engine", desc: "4-module financial suite", badge: "NEW" },
        { to: "/tools/legal-financing", label: "Legal Financing", desc: "Litigation funding & pre-settlement", badge: "NEW" },
        { to: "/tools/settlement", label: "Settlement Calc", desc: "Value estimation" },
        { to: "/tools/venue", label: "Venue Comparison", desc: "Compare jurisdictions" },
      ]
    },
    {
      title: "Companeze Business Services",
      desc: "LLC formation, compliance & entity advisory",
      color: "from-slate-700 to-slate-800",
      icon: Building2,
      tools: [
        { to: "/tools/companeze", label: "Companeze", desc: "Business formation & compliance", badge: "NEW" },
        { to: "/tools/partner-portal", label: "Partner Portal", desc: "Corporate services for lawyers", badge: "NEW" },
      ]
    },
    {
      title: "Damages & Fraud Analytics",
      desc: "Forensic damages calculations & fraud detection",
      color: "from-orange-600 to-red-700",
      icon: Shield,
      tools: [
        { to: "/tools/damages-calculator", label: "Damages Calculator", desc: "Economic, non-economic & punitive", badge: "NEW" },
        { to: "/tools/fraud-analytics", label: "Fraud Analytics", desc: "Benford's law & pattern detection", badge: "NEW" },
        { to: "/tools/court-forms", label: "Court Forms Hub", desc: "All districts, auto-fill & download", badge: "NEW" },
        { to: "/tools/triple-shield", label: "Triple Shield", desc: "SelleShield + AML compliance", badge: "NEW" },
      ]
    },
    {
      title: "Calculators & Utilities",
      desc: "Deadlines, SOL & specialized tools",
      color: "from-teal-600 to-teal-700",
      icon: Calculator,
      tools: [
        { to: "/tools/deadlines", label: "Deadlines", desc: "Court deadline calc" },
        { to: "/tools/sol", label: "Statute of Limitations", desc: "SOL deadline lookup" },
        { to: "/tools/precedent-finder", label: "Precedent Finder", desc: "AI case research" },
        { to: "/tools/trend-analyzer", label: "Trend Analyzer", desc: "Litigation trends" },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Shield className="h-7 w-7 text-blue-400" />
                <h1 className="text-xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Command Center</h1>
              </div>
              <p className="text-sm text-slate-400">Welcome back, {user?.name}</p>
            </div>
            <Link to="/tools/case-starter">
              <Button className="bg-blue-600 hover:bg-blue-500" data-testid="new-case-btn">
                <Plus className="h-4 w-4 mr-1.5" /> Start New Case
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Platform Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { label: "Cases", value: stats.total_cases || 0, icon: Briefcase, color: "text-blue-600" },
            { label: "AI Outputs", value: stats.total_ai_outputs || 0, icon: Sparkles, color: "text-purple-600" },
            { label: "Reports", value: stats.total_reports || 0, icon: FileText, color: "text-emerald-600" },
            { label: "Scorecards", value: stats.total_scorecards || 0, icon: Shield, color: "text-amber-600" },
            { label: "Investigations", value: stats.total_investigations || 0, icon: Eye, color: "text-red-600" },
            { label: "Requests", value: stats.total_legal_requests || 0, icon: MessageSquare, color: "text-teal-600" },
            { label: "Verified", value: stats.total_verifications || 0, icon: CheckCircle2, color: "text-green-600" },
          ].map((s, i) => (
            <Card key={i} className="border-0 shadow-sm" data-testid={`stat-${s.label.toLowerCase()}`}>
              <CardContent className="pt-3 pb-3 px-3 text-center">
                <s.icon className={`h-4 w-4 ${s.color} mx-auto mb-1`} />
                <p className="text-lg font-bold">{s.value}</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tool Categories Grid */}
        <div>
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Fraunces', serif" }}>Platform Tools</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {TOOL_CATEGORIES.map((cat, ci) => (
              <Card key={ci} className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`tool-category-${cat.title.toLowerCase().replace(/ /g,'-')}`}>
                <div className={`bg-gradient-to-r ${cat.color} px-4 py-3 flex items-center gap-2`}>
                  <cat.icon className="h-4 w-4 text-white/80" />
                  <div>
                    <h3 className="text-sm font-semibold text-white">{cat.title}</h3>
                    <p className="text-[10px] text-white/60">{cat.desc}</p>
                  </div>
                </div>
                <CardContent className="p-2">
                  <div className="grid grid-cols-2 gap-1.5">
                    {cat.tools.map((tool, ti) => (
                      <Link key={ti} to={tool.to}>
                        <div className="p-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group" data-testid={`tool-link-${tool.label.toLowerCase().replace(/ /g,'-')}`}>
                          <div className="flex items-center gap-1 mb-0.5">
                            <p className="text-xs font-medium group-hover:text-blue-600 transition-colors">{tool.label}</p>
                            {tool.badge && <Badge className={`text-[8px] px-1 py-0 h-3.5 ${tool.badge === 'LIVE' ? 'bg-green-500' : tool.badge === 'CORE' ? 'bg-blue-500' : 'bg-red-500'}`}>{tool.badge}</Badge>}
                          </div>
                          <p className="text-[10px] text-slate-400">{tool.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Recent Cases + Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Cases */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-blue-600" /> Recent Cases
                </CardTitle>
                <Link to="/tools/litix-wizard">
                  <Button size="sm" variant="ghost" className="text-xs h-7"><Plus className="h-3 w-3 mr-1" />New</Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {(data?.recent_cases || []).length === 0 ? (
                <div className="text-center py-6">
                  <FolderOpen className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">No cases yet</p>
                  <Link to="/tools/litix-wizard">
                    <Button size="sm" className="mt-3"><Plus className="h-3 w-3 mr-1" />Start First Case</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.recent_cases.map((c, i) => (
                    <Link key={i} to={`/case/${c.case_id}`} data-testid={`recent-case-${i}`}>
                      <div className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{c.case_name || "Untitled"}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {c.state && <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{c.state}</span>}
                            {c.court_type && <span className="text-[10px] text-slate-400 capitalize">{c.court_type}</span>}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-slate-300" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Practice Suite Activity */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-600" /> Practice Suite Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Statements", value: ps.statements || 0, icon: Gavel, to: "/tools/opening" },
                  { label: "Settlements", value: ps.settlements || 0, icon: Scale, to: "/tools/settlement-negotiation" },
                  { label: "Insurance", value: ps.insurance_reports || 0, icon: Shield, to: "/tools/insurance-report" },
                  { label: "Demand Letters", value: ps.demand_letters || 0, icon: FileText, to: "/tools/demand-letter" },
                  { label: "Depositions", value: ps.depositions || 0, icon: Users, to: "/tools/deposition" },
                  { label: "Motions", value: ps.motions || 0, icon: BookOpen, to: "/tools/motion" },
                  { label: "Marketing", value: stats.marketing_content || 0, icon: Globe, to: "/tools/marketing" },
                  { label: "Government", value: stats.government_responses || 0, icon: Building2, to: "/tools/government" },
                ].map((item, i) => (
                  <Link key={i} to={item.to}>
                    <div className="flex items-center gap-2.5 p-2.5 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`activity-${item.label.toLowerCase()}`}>
                      <item.icon className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-xs font-medium">{item.label}</p>
                        <p className="text-lg font-bold leading-tight">{item.value}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Intelligence */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Recent Scorecards */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="h-4 w-4 text-amber-600" /> Carrier Scorecards
                </CardTitle>
                <Link to="/tools/carrier-scorecard">
                  <Button size="sm" variant="ghost" className="text-xs h-7">New <ArrowRight className="h-3 w-3 ml-1" /></Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {(data?.recent_scorecards || []).length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-400">No scorecards yet</p>
                  <Link to="/tools/carrier-scorecard"><Button size="sm" variant="outline" className="mt-2 text-xs">Analyze Carrier</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.recent_scorecards.map((sc, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                      <div>
                        <p className="text-sm font-medium">{sc.carrier_name}</p>
                        <p className="text-[10px] text-slate-400">{sc.state} - {sc.claim_type}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={`text-xs ${sc.overall_grade?.startsWith('A') ? 'bg-green-500' : sc.overall_grade?.startsWith('B') ? 'bg-blue-500' : sc.overall_grade?.startsWith('C') ? 'bg-amber-500' : 'bg-red-500'}`}>
                          {sc.overall_grade || "N/A"}
                        </Badge>
                        <p className="text-[10px] text-slate-400 mt-0.5">Score: {sc.overall_score || 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Investigations */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4 text-red-600" /> Investigations
                </CardTitle>
                <Link to="/tools/investigator">
                  <Button size="sm" variant="ghost" className="text-xs h-7">New <ArrowRight className="h-3 w-3 ml-1" /></Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {(data?.recent_investigations || []).length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-400">No investigations yet</p>
                  <Link to="/tools/investigator"><Button size="sm" variant="outline" className="mt-2 text-xs">Start Investigation</Button></Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {data.recent_investigations.map((inv, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50">
                      <div>
                        <p className="text-sm font-medium">{inv.subject?.name || inv.party_name || "Subject"}</p>
                        <p className="text-[10px] text-slate-400 capitalize">{inv.subject?.type || inv.party_type || "Individual"}</p>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize">{inv.risk_profile || inv.status || "complete"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Additional Services Row */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" /> Additional Services
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { to: "/tools/lytix", label: "Lytix Ledger", desc: "Document analysis", icon: FileText, color: "text-blue-600" },
                { to: "/tools/strategy", label: "Strategy Patterns", desc: "Case strategy", icon: Target, color: "text-purple-600" },
                { to: "/tools/watchlist", label: "Watchlist", desc: "Monitor entities", icon: Eye, color: "text-orange-600" },
                { to: "/tools/insurance", label: "Insurance Center", desc: "Insurance tools", icon: Shield, color: "text-emerald-600" },
                { to: "/tools/services-hub", label: "Service Networks", desc: "Professional services", icon: Truck, color: "text-teal-600" },
              ].map((s, i) => (
                <Link key={i} to={s.to}>
                  <div className="flex flex-col items-center gap-1.5 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer text-center" data-testid={`service-${s.label.toLowerCase().replace(/ /g,'-')}`}>
                    <s.icon className={`h-5 w-5 ${s.color}`} />
                    <p className="text-xs font-medium">{s.label}</p>
                    <p className="text-[10px] text-slate-400">{s.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
